import { LitElement, html, nothing, css, unsafeCSS } from 'lit';
import styles from './style.scss?inline';
import { DEFAULT_LABEL_FORMAT, EVENTS, PLACEHOLDERS } from './constants';
import { EVENTS as SELECT_EVENTS } from '../../select/constants.js';

export default class SotbitEditableField extends LitElement {
    static styles = css`
        ${unsafeCSS(styles)}
    `;

    static properties = {
        label: { type: String },
        labelFormat: { type: String, attribute: 'label-format' },
        placeholder: { type: String },
        input: { state: true },
        _isEditing: { state: true },
    };

    constructor() {
        super();

        this.placeholder = html`&mdash;`;
        this.labelFormat = DEFAULT_LABEL_FORMAT;
        this.input = null;
        this._isEditing = false;
        this._valueBeforeEdit = null;
    }

    render() {
        return html`
            <div part=${this._isEditing ? 'preview hidden' : 'preview'}>${this._renderPreview()}</div>
            <slot part=${this._isEditing ? nothing : 'hidden'} @slotchange=${this._handleSlotChange}></slot>
        `;
    }

    edit(dispatchEvent = true) {
        if (!this.input || this._isEditing) {
            return;
        }

        if (
            dispatchEvent &&
            !this.dispatchEvent(
                new CustomEvent(EVENTS.START_EDIT, {
                    bubbles: true,
                    composed: true,
                    cancelable: true,
                })
            )
        ) {
            return;
        }

        this.input.calculatePlaceholderShow?.();
        this.input.calculateSmallLabel?.();

        this._valueBeforeEdit = this.input.value;
        this._isEditing = true;
    }

    /**
     * @throws Error
     */
    save(dispatchEvent = true) {
        if (!this.input || !this._isEditing) {
            return;
        }

        if (
            dispatchEvent &&
            !this.dispatchEvent(
                new CustomEvent(EVENTS.SAVE_EDIT, {
                    bubbles: true,
                    composed: true,
                    cancelable: true,
                })
            )
        ) {
            return;
        }

        if (this.input.errorText) {
            throw new Error(this.input.errorText);
        }

        this._isEditing = false;
    }

    cancel(dispatchEvent = true) {
        if (!this.input || !this._isEditing) {
            return;
        }

        if (
            dispatchEvent &&
            !this.dispatchEvent(
                new CustomEvent(EVENTS.CANCEL_EDIT, {
                    bubbles: true,
                    composed: true,
                    cancelable: true,
                })
            )
        ) {
            return;
        }

        if (this.input.error !== undefined) {
            this.input.error = false;
        }

        this.input.errorText = false;
        this.input.value = this._valueBeforeEdit;
        this._valueBeforeEdit = null;
        this._isEditing = false;
    }

    getLabel() {
        const labelRaw = this.input.label || this.label;

        if (!labelRaw) {
            return '';
        }

        const labelFormat = typeof this.labelFormat === 'string' ? this.labelFormat : DEFAULT_LABEL_FORMAT;

        return labelFormat.replace(PLACEHOLDERS.LABEL, labelRaw);
    }

    connectedCallback() {
        super.connectedCallback();

        this.addEventListener(SELECT_EVENTS.OPTIONS_CHANGE, this._handleOptionsChange);
    }

    _handleOptionsChange = () => {
        this.requestUpdate();
    };

    _renderPreview() {
        let displayValue = this._getDisplayValue(this._getRealValue());

        if (!(typeof displayValue === 'string')) {
            return;
        }

        if (!displayValue) {
            displayValue = this.placeholder;
        }

        const label = this.getLabel();

        return html`
            ${label ? html`<span part="label">${label}</span>` : nothing}
            <span part="value">${displayValue}</span>
        `;
    }

    _getDisplayValue(value) {
        if (value === undefined || value === null) {
            return null;
        }

        if (Array.isArray(value)) {
            return value
                .map((option) => option.textContent.trim())
                .sort()
                .join(' / ');
        }

        return value;
    }

    _getRealValue() {
        switch (this.input?.type) {
            case 'select-one':
            case 'select-multiple':
                return this.input?.selectedOptions;
            default:
                return this.input?.value;
        }
    }

    _handleSlotChange() {
        const slot = this.shadowRoot.querySelector('slot');

        this.input = slot?.assignedElements({ flatten: true })?.[0] || null;

        if (!this.input) {
            return;
        }

        this.requestUpdate();
    }
}
